import { FlagOutlined, VisibilityOffRounded } from "@mui/icons-material";
import { Button, Link, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { CreateIssueModal, IssueStatusPicker, isWhitespace, ItemProperty } from "..";
import { DEFAULT_ISSUE_STATUS, IssuePrivacyType, Links, SupportingItemType } from "../../constants";
import { SupportingIssueDto } from "../../http";
import { useState } from "react";

interface IProps {
  items: SupportingIssueDto[];
  context?: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  isLoading?: boolean;
  onClick?: (type: SupportingItemType, issueItemGuid: string) => void;
}

const SupportingIssuesProperty = (props: IProps) => {
  const [isCreateIssueModalVisible, setIsCreateIssueModalVisible] = useState(false);
  const theme = useTheme();

  function handleClicked(event: React.MouseEvent, issueItemGuid: string) {
    if (props.onClick) {
      event.preventDefault();
      props.onClick(SupportingItemType.Issue, issueItemGuid);
    }
  }

  return (
    <ItemProperty labelIcon={<FlagOutlined />} label="Issues" labelProps={{ sx: { alignSelf: "start" } }}>
      <Stack spacing={0.5}>
        {props.isLoading ? (
          <Stack spacing={0.5}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={24} width={Math.random() * 175 + 75} />
            ))}
          </Stack>
        ) : (
          props.items.map((issue) => (
            <Stack direction="row" key={issue.issueGuid} alignItems="center">
              <IssueStatusPicker
                issueGuid={issue.issueGuid}
                status={issue.currentStatus ?? DEFAULT_ISSUE_STATUS}
                variant="button"
                disabled={true}
              />
              <Stack direction="row" spacing={0.25} alignItems="center">
                <Tooltip
                  title={
                    <Typography>
                      {issue.team && (
                        <>
                          <strong>{issue.team.name}</strong>
                          <span> : </span>
                        </>
                      )}
                      <Link href={Links.IssueDetail(issue.issueGuid)}>{issue.description}</Link>
                    </Typography>
                  }
                  placement="right"
                >
                  <Link href={Links.IssueDetail(issue.issueGuid)} onClick={(e) => handleClicked(e, issue.issueGuid)}>
                    {issue.description == null || isWhitespace(issue.description) ? (
                      <Typography variant="placeholder">Nothing here yet.</Typography>
                    ) : (
                      <Typography>{issue.description}</Typography>
                    )}
                  </Link>
                </Tooltip>

                {issue.privacy === IssuePrivacyType.Private && (
                  <Tooltip
                    title="Private to Team Members and Issue Creator"
                    slotProps={{
                      tooltip: {
                        sx: {
                          color: theme.palette.error.main,
                        },
                      },
                    }}
                  >
                    <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          ))
        )}

        <Button
          variant="tertiary"
          startIcon={<FlagOutlined fontSize="small" />}
          sx={{ alignSelf: "start", whiteSpace: "nowrap" }}
          onClick={() => setIsCreateIssueModalVisible(true)}
        >
          Add
        </Button>
      </Stack>

      <CreateIssueModal
        visible={isCreateIssueModalVisible}
        context={props.context}
        onCreated={() => setIsCreateIssueModalVisible(false)}
        onCancelled={() => setIsCreateIssueModalVisible(false)}
      />
    </ItemProperty>
  );
};

export { SupportingIssuesProperty };
